// OSIS Cloud Files Manager
import React, { useState, useEffect } from 'react';
import {
  Folder,
  File,
  Upload,
  Download,
  Trash2,
  Share2,
  Search,
  Filter,
  Grid,
  List,
  SortAsc,
  SortDesc,
  Plus,
  Eye,
  Edit,
  Copy,
  Move,
  Lock,
  Unlock,
  Star,
  Clock,
  User,
  Calendar,
  FileText,
  Image,
  Video,
  Music,
  Archive,
  MoreHorizontal,
  ArrowLeft,
  Home,
  ChevronRight,
  Info,
  Zap
} from 'lucide-react';

const CloudFiles = () => {
  const [currentPath, setCurrentPath] = useState('/');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [sortBy, setSortBy] = useState('name'); // name, date, size, type
  const [sortOrder, setSortOrder] = useState('asc'); // asc or desc
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [breadcrumbs, setBreadcrumbs] = useState([
    { name: 'Cloud OSIS', path: '/' }
  ]);

  const [folders, setFolders] = useState([
    {
      id: 1,
      name: 'Proiecte 2025',
      type: 'folder',
      size: '--',
      modified: '2025-01-15 14:30',
      items: 23,
      owner: 'Adrian Popescu',
      shared: true,
      favorite: true
    },
    {
      id: 2,
      name: 'Documente Contracte',
      type: 'folder',
      size: '--',
      modified: '2025-01-14 16:45',
      items: 89,
      owner: 'Maria Ionescu',
      shared: false,
      favorite: false
    },
    {
      id: 3,
      name: 'Fotografii Montaj',
      type: 'folder',
      size: '--',
      modified: '2025-01-13 11:20',
      items: 156,
      owner: 'Mihai Georgescu',
      shared: true,
      favorite: true
    },
    {
      id: 4,
      name: 'Archive Backup',
      type: 'folder',
      size: '--',
      modified: '2025-01-12 09:15',
      items: 45,
      owner: 'System',
      shared: false,
      favorite: false
    }
  ]);

  const [files, setFiles] = useState([
    {
      id: 5,
      name: 'Contract_Ionescu_Brasov.pdf',
      type: 'pdf',
      size: '2.3 MB',
      modified: '2025-01-15 14:30',
      owner: 'Adrian Popescu',
      shared: true,
      favorite: false,
      encrypted: true
    },
    {
      id: 6,
      name: 'Masuratori_Casa_Cluj.xlsx',
      type: 'xlsx',
      size: '1.8 MB',
      modified: '2025-01-15 13:15',
      owner: 'Maria Ionescu',
      shared: false,
      favorite: true,
      encrypted: true
    },
    {
      id: 7,
      name: 'Prezentare_OSIS_2025.pptx',
      type: 'pptx',
      size: '15.2 MB',
      modified: '2025-01-14 10:45',
      owner: 'Mihai Georgescu',
      shared: true,
      favorite: false,
      encrypted: false
    },
    {
      id: 8,
      name: 'Video_Montaj_Suceava.mp4',
      type: 'mp4',
      size: '45.7 MB',
      modified: '2025-01-13 16:20',
      owner: 'Mihai Georgescu',
      shared: true,
      favorite: true,
      encrypted: false
    },
    {
      id: 9,
      name: 'Fotografie_Finalizare_Proiect.jpg',
      type: 'jpg',
      size: '5.2 MB',
      modified: '2025-01-12 14:35',
      owner: 'Adrian Popescu',
      shared: false,
      favorite: false,
      encrypted: false
    }
  ]);

  const getFileIcon = (type, size = 'w-5 h-5') => {
    const iconClasses = `${size}`;
    
    switch (type) {
      case 'folder':
        return <Folder className={`${iconClasses} text-blue-600`} />;
      case 'pdf':
        return <FileText className={`${iconClasses} text-red-600`} />;
      case 'doc':
      case 'docx':
        return <FileText className={`${iconClasses} text-blue-600`} />;
      case 'xls':
      case 'xlsx':
        return <FileText className={`${iconClasses} text-green-600`} />;
      case 'ppt':
      case 'pptx':
        return <FileText className={`${iconClasses} text-orange-600`} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image className={`${iconClasses} text-purple-600`} />;
      case 'mp4':
      case 'avi':
      case 'mov':
        return <Video className={`${iconClasses} text-indigo-600`} />;
      case 'mp3':
      case 'wav':
        return <Music className={`${iconClasses} text-pink-600`} />;
      case 'zip':
      case 'rar':
        return <Archive className={`${iconClasses} text-yellow-600`} />;
      default:
        return <File className={`${iconClasses} text-gray-600`} />;
    }
  };

  const toggleSelection = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const allItems = [...folders, ...files];

  const ActionButton = ({ icon: Icon, label, onClick, className = "" }) => (
    <button
      onClick={onClick}
      className={`flex items-center px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${className}`}
      title={label}
    >
      <Icon className="w-4 h-4 mr-2" />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Fișiere & Documente
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Gestionează fișierele companiei în cloud
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Fișiere
              </button>
              <button className="flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium rounded-lg transition-colors">
                <Plus className="w-4 h-4 mr-2" />
                Folder Nou
              </button>
            </div>
          </div>
        </div>

        {/* Breadcrumbs */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
          <Home className="w-4 h-4" />
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <button 
                onClick={() => setCurrentPath(crumb.path)}
                className="hover:text-cyan-600 transition-colors"
              >
                {crumb.name}
              </button>
              {index < breadcrumbs.length - 1 && <ChevronRight className="w-4 h-4" />}
            </React.Fragment>
          ))}
        </div>

        {/* Toolbar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search & Filter */}
            <div className="flex items-center space-x-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Caută fișiere..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
              <button className="flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                <Filter className="w-4 h-4 mr-2" />
                Filtre
              </button>
            </div>

            {/* View Controls */}
            <div className="flex items-center space-x-4">
              {/* Sort */}
              <div className="flex items-center space-x-2">
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                >
                  <option value="name">Nume</option>
                  <option value="date">Data</option>
                  <option value="size">Mărime</option>
                  <option value="type">Tip</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
                </button>
              </div>

              {/* View Mode */}
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid' 
                      ? 'bg-white dark:bg-gray-800 text-cyan-600 shadow-soft' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-white dark:bg-gray-800 text-cyan-600 shadow-soft' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Selected Items Actions */}
          {selectedItems.length > 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedItems.length} element{selectedItems.length !== 1 ? 'e' : ''} selectat{selectedItems.length !== 1 ? 'e' : ''}
              </div>
              <div className="flex items-center space-x-2">
                <ActionButton icon={Download} label="Download" />
                <ActionButton icon={Share2} label="Partajează" />
                <ActionButton icon={Move} label="Mută" />
                <ActionButton icon={Copy} label="Copiază" />
                <ActionButton icon={Trash2} label="Șterge" className="text-red-600 hover:text-red-700" />
              </div>
            </div>
          )}
        </div>

        {/* Files Grid/List */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {allItems.map((item) => (
              <div
                key={item.id}
                className={`bg-white dark:bg-gray-800 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                  selectedItems.includes(item.id)
                    ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                onClick={() => toggleSelection(item.id)}
              >
                <div className="p-4 text-center">
                  <div className="flex justify-center mb-3">
                    {getFileIcon(item.type, 'w-10 h-10')}
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1 truncate" title={item.name}>
                    {item.name}
                  </h3>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {item.type === 'folder' ? `${item.items} elemente` : item.size}
                  </div>
                  <div className="flex items-center justify-center mt-2 space-x-1">
                    {item.favorite && <Star className="w-3 h-3 text-yellow-500" />}
                    {item.shared && <Share2 className="w-3 h-3 text-blue-500" />}
                    {item.encrypted && <Lock className="w-3 h-3 text-green-500" />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems(allItems.map(item => item.id));
                          } else {
                            setSelectedItems([]);
                          }
                        }}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Nume
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Mărime
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Modificat
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Proprietar
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acțiuni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {allItems.map((item) => (
                    <tr 
                      key={item.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                        selectedItems.includes(item.id) ? 'bg-cyan-50 dark:bg-cyan-900/20' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input 
                          type="checkbox" 
                          checked={selectedItems.includes(item.id)}
                          onChange={() => toggleSelection(item.id)}
                          className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 mr-3">
                            {getFileIcon(item.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {item.name}
                              </p>
                              <div className="flex items-center ml-2 space-x-1">
                                {item.favorite && <Star className="w-3 h-3 text-yellow-500" />}
                                {item.shared && <Share2 className="w-3 h-3 text-blue-500" />}
                                {item.encrypted && <Lock className="w-3 h-3 text-green-500" />}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {item.type === 'folder' ? `${item.items} elemente` : item.size}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {item.modified}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {item.owner}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <Download className="w-4 h-4" />
                          </button>
                          <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <Share2 className="w-4 h-4" />
                          </button>
                          <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* AWS S3 Status */}
        <div className="mt-8">
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-xl border border-orange-200 dark:border-orange-800 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Zap className="w-5 h-5 text-orange-600 mr-3" />
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Sincronizare Amazon S3
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Ultimul sync: Acum 5 minute • Status: <span className="text-green-600 font-medium">Online</span>
                  </p>
                </div>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Bucket: osis-storage-ro
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudFiles;
